import React, { useCallback, useMemo } from 'react';
import { Download } from 'lucide-react';
import { reportsAPI } from '../../../services/api';
import useReport from '../shared/useReport';
import KpiStrip from '../shared/KpiStrip';
import PrintButton from '../shared/PrintButton';
import { exportCsv } from '../shared/exportCsv';
import { clinicToday } from '../../../utils/clinicTime';
import RegisterGrid, { MonthPicker, monthKey, monthLabel } from '../../attendance/RegisterGrid';
import { STATUS_LEGEND, statusOf } from '../../attendance/attendanceStatus';
import useLocalState from './useLocalState';

// The month's attendance as a single sheet, the way a register is kept on paper: a row per person,
// a letter per day. The same grid the attendance board draws, for people who have Reports but not
// the board itself. Downloads as one CSV row per person, a column per day, so it opens in Excel
// looking like the sheet on screen.

const AttendanceRegisterReport = () => {
  const [month, setMonth] = useLocalState('attendance-register-month', () => monthKey(clinicToday()));

  const fetcher = useCallback(() => reportsAPI.getAttendanceRegister(month), [month]);
  const { data, loading } = useReport(fetcher);

  const totals = useMemo(() => {
    const rows = data?.rows || [];
    const sum = (f) => rows.reduce((n, r) => n + Number(f(r) || 0), 0);
    return {
      staff: rows.length,
      present: sum((r) => r.totals.present),
      absent: sum((r) => r.totals.absent),
      late: sum((r) => r.totals.late),
      leave: sum((r) => r.totals.leaveDays),
      hours: sum((r) => r.totals.hours),
    };
  }, [data]);

  const kpis = [
    { label: 'Staff', value: totals.staff, sub: 'People on the register' },
    { label: 'Present days', value: totals.present, sub: 'Full days worked' },
    { label: 'Absent days', value: totals.absent, sub: 'Working days with nothing recorded', tone: totals.absent > 0 ? 'alert' : undefined },
    { label: 'Late days', value: totals.late, sub: 'Punched in after the grace time' },
    { label: 'Leave days', value: totals.leave, sub: 'Approved leave in the month' },
  ];

  const download = () => {
    if (!data) return;
    const columns = [
      { key: 'userName', label: 'Name' },
      { key: 'role', label: 'Role' },
      ...data.days.map((d) => ({
        key: d.date,
        label: d.date.slice(8),
        csv: (r) => statusOf(r.days[d.date]).short || '',
      })),
      { key: 'present', label: 'Present', csv: (r) => r.totals.present },
      { key: 'halfDays', label: 'Half days', csv: (r) => r.totals.halfDays },
      { key: 'absent', label: 'Absent', csv: (r) => r.totals.absent },
      { key: 'leaveDays', label: 'Leave', csv: (r) => r.totals.leaveDays ?? 0 },
      { key: 'late', label: 'Late', csv: (r) => r.totals.late },
      { key: 'hours', label: 'Hours', csv: (r) => r.totals.hours },
    ];
    exportCsv(`attendance-register-${month}`, columns, data.rows);
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl shadow p-4 flex items-center justify-between gap-3 flex-wrap">
        <MonthPicker month={month} onMonth={setMonth} max={monthKey(clinicToday())} />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={download}
            disabled={!data}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 print:hidden"
          >
            <Download className="w-4 h-4" aria-hidden="true" /> CSV
          </button>
          <PrintButton />
        </div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <div className="space-y-2">
        <p className="text-xs text-gray-500">{STATUS_LEGEND} · a dot marks a punch that needs a look</p>
        {loading || !data ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center text-sm text-gray-500">
            {loading ? 'Loading the register…' : 'Nothing to show.'}
          </div>
        ) : (
          <RegisterGrid data={data} />
        )}
      </div>

      <p className="text-xs text-gray-500">
        {monthLabel(month)}. Each person is judged by their own hours, so a day off for one person can be a working day for another.
      </p>
    </div>
  );
};

export default AttendanceRegisterReport;
